// Accroche, intro et générique d'une animation : AJOUTÉS PAR LE LECTEUR.
//
//   accroche  : VIDÉO seulement, avant tout : la question que tout élève se
//               pose (« C'est quoi, Pythagore ??? ») — le seul texte écrit
//               par l'IA (`bookends.hook`), l'image est celle du lecteur ;
//   intro     : logo JeRaisonne « présente », titre, niveau et matière ;
//   générique : logo, voix (« Voix : … », d'après `speaker` des voix
//               enregistrées) et, pour la vidéo publiée, un message qui
//               donne envie de venir (« Défie tes potes ! … ») — varié d'une
//               animation à l'autre, toujours le même pour une animation.
//
// Ce sont des scènes JRAF ordinaires (narration, durée, voix enregistrée
// possible) aux identifiants réservés `jr-hook`, `jr-intro` et `jr-outro` ;
// leur image est dessinée par le lecteur (`BookendLayer`). Un document ne
// peut pas utiliser le préfixe `jr-` (validation : `reserved_id`).

import type { JrafDocument, JrafScene } from './schema/document';

export const HOOK_SCENE_ID = 'jr-hook';
export const INTRO_SCENE_ID = 'jr-intro';
export const OUTRO_SCENE_ID = 'jr-outro';
/** Préfixe des identifiants ajoutés par le lecteur. */
export const RESERVED_ID_PREFIX = 'jr-';

/** `app` : dans l'application ; `video` : vidéo publiée (Shorts, TikTok). */
export type BookendMode = 'app' | 'video';

/** Durée minimale de l'accroche, de l'intro et du générique (ms) ; la voix peut les allonger. */
export const HOOK_MS = 2800;
export const INTRO_MS = 3800;
export const OUTRO_MS = 4200;

export const APP_FAREWELL_LINE = 'À bientôt sur JeRaisonne !';

/** Message du générique : une accroche courte (en or) et une phrase qui mène à l'app. */
export interface OutroMessage {
  punch: string | null;
  line: string;
}

/**
 * Fins de la vidéo publiée : donner envie de venir s'entraîner sur l'app.
 * Variées d'une animation à l'autre (`videoOutro`).
 */
export const VIDEO_OUTROS: readonly { punch: string; line: string }[] = [
  { punch: 'À toi de jouer !', line: 'Viens t’entraîner sur JeRaisonne.' },
  { punch: 'Défie tes potes !', line: 'Qui ira le plus loin sur JeRaisonne ?' },
  { punch: 'T’as tout compris ?', line: 'Prouve-le sur JeRaisonne !' },
  { punch: 'Un contrôle bientôt ?', line: 'Prépare-le sur JeRaisonne.' },
  { punch: 'Lance le défi à ta classe !', line: 'Rendez-vous sur JeRaisonne.' },
  { punch: 'Trop facile ?', line: 'Passe au niveau au-dessus sur JeRaisonne.' },
  { punch: 'Ne t’arrête pas là !', line: 'La suite t’attend sur JeRaisonne.' },
  { punch: 'Tu veux progresser ?', line: 'Entraîne-toi chaque jour sur JeRaisonne.' },
];

/** Empreinte stable d'un identifiant (FNV-1a 32 bits). */
function fingerprint(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Espace fine insécable avant ? ! : ; (le signe ne part pas seul à la ligne). */
export function frenchSpacing(text: string): string {
  return text.replace(/[ \u00a0]([?!:;])/g, '\u202f$1');
}

/** Fin de la vidéo d'une animation : la même à chaque lecture, une autre pour l'animation suivante. */
export function videoOutro(docId: string): OutroMessage {
  const o = VIDEO_OUTROS[fingerprint(docId) % VIDEO_OUTROS.length]!;
  return { punch: frenchSpacing(o.punch), line: frenchSpacing(o.line) };
}

/** Message du générique selon le mode. */
export function outroMessage(doc: Pick<JrafDocument, 'id'>, mode: BookendMode): OutroMessage {
  return mode === 'video'
    ? videoOutro(doc.id)
    : { punch: null, line: frenchSpacing(APP_FAREWELL_LINE) };
}

export type BookendKind = 'hook' | 'intro' | 'outro';

export function bookendOf(sceneId: string): BookendKind | null {
  return sceneId === HOOK_SCENE_ID
    ? 'hook'
    : sceneId === INTRO_SCENE_ID
      ? 'intro'
      : sceneId === OUTRO_SCENE_ID
        ? 'outro'
        : null;
}

/** Titre sans ponctuation de fin de phrase (une phrase = un sous-titre). */
function titleForSentence(title: string): string {
  return title
    .replace(/[.!?…]+(?=\s)/g, ',')
    .replace(/[.!?…:;,\s]+$/g, '')
    .trim();
}

function hasMath(text: string): boolean {
  return /\$|\\[a-zA-Z]/.test(text);
}

/** « JeRaisonne » tel que le dit la voix du navigateur. */
function spoken(text: string): string {
  return text.replace(/JeRaisonne/g, 'Je raisonne');
}

/** Ce que disent l'accroche (vidéo, si le document en a une), l'intro et le générique. */
export function bookendNarrations(
  doc: Pick<JrafDocument, 'title'> & Partial<Pick<JrafDocument, 'id' | 'bookends'>>,
  mode: BookendMode,
): {
  hook: { text: string; say: string } | null;
  intro: { text: string; say: string };
  outro: { text: string; say: string };
} {
  const title = titleForSentence(doc.title);
  const spokenTitle = hasMath(title) ? 'une nouvelle leçon' : title;
  const hook = mode === 'video' ? doc.bookends?.hook : undefined;
  const outro = outroMessage({ id: doc.id ?? doc.title }, mode);
  const outroText = outro.punch ? `${outro.punch} ${outro.line}` : outro.line;
  return {
    hook: hook ? { text: hook.text, say: hook.say ?? hook.text } : null,
    intro: {
      text: `JeRaisonne présente : ${title}.`,
      say: `Je raisonne présente : ${spokenTitle}.`,
    },
    outro: { text: outroText, say: spoken(outroText) },
  };
}

/** Ce que le générique écrit pour une voix de synthèse (vidéo exportée, lot 7). */
export const SYNTHETIC_SPEAKER = 'voix de synthèse';

/**
 * Qui parle dans l'animation (voix utilisées), dans l'ordre ; une voix de
 * synthèse (export vidéo) est nommée `SYNTHETIC_SPEAKER`, en dernier.
 */
export function voiceSpeakers(doc: Pick<JrafDocument, 'assets' | 'scenes' | 'bookends'>): string[] {
  const used = new Set<string>();
  for (const s of doc.scenes) if (s.narration.audioAssetId) used.add(s.narration.audioAssetId);
  if (doc.bookends?.hookAudioAssetId) used.add(doc.bookends.hookAudioAssetId);
  if (doc.bookends?.introAudioAssetId) used.add(doc.bookends.introAudioAssetId);
  if (doc.bookends?.outroAudioAssetId) used.add(doc.bookends.outroAudioAssetId);
  const out: string[] = [];
  let synthetic = false;
  for (const a of doc.assets) {
    if (a.kind !== 'audio' || !used.has(a.id)) continue;
    if (a.voice === 'synthetic') synthetic = true;
    else if (a.speaker && !out.includes(a.speaker)) out.push(a.speaker);
  }
  if (synthetic) out.push(SYNTHETIC_SPEAKER);
  return out;
}

/**
 * Ligne du générique : « Voix : Philippe », « Voix : Philippe et une voix de
 * synthèse », « Voix de synthèse » ; `null` sans voix nommée.
 */
export function speakerCredit(speakers: readonly string[]): string | null {
  const people = speakers.filter((s) => s !== SYNTHETIC_SPEAKER);
  const synthetic = people.length < speakers.length;
  if (people.length === 0) return synthetic ? 'Voix de synthèse' : null;
  const names =
    people.length === 1
      ? people[0]!
      : `${people.slice(0, -1).join(', ')} et ${people[people.length - 1]!}`;
  return synthetic ? `Voix : ${names} et une voix de synthèse` : `Voix : ${names}`;
}

function bookendScene(
  id: string,
  title: string,
  narration: { text: string; say: string },
  audioAssetId: string | null | undefined,
  holdMs: number,
): JrafScene {
  return {
    id,
    title,
    interactive: false,
    narration: { ...narration, ...(audioAssetId ? { audioAssetId } : {}) },
    steps: [{ id: 'jr-hold', action: 'timeline.wait', durationMs: holdMs }],
    advance: { mode: 'auto' },
    transition: 'fade',
  };
}

/**
 * Document joué : [accroche (vidéo)] + intro + scènes du document +
 * générique. Pur ; le document reçu (validé, déjà privé de sa partie payante
 * s'il le faut) n'est pas modifié.
 */
export function withBookends(doc: JrafDocument, opts: { mode?: BookendMode } = {}): JrafDocument {
  if (doc.scenes.some((s) => bookendOf(s.id))) return doc; // déjà fait
  const mode = opts.mode ?? 'app';
  const n = bookendNarrations(doc, mode);
  const hook = n.hook
    ? [
        {
          ...bookendScene(
            HOOK_SCENE_ID,
            'Accroche',
            n.hook,
            doc.bookends?.hookAudioAssetId,
            HOOK_MS,
          ),
          // la question claque, puis la marque se présente
          transition: 'cut' as const,
        },
      ]
    : [];
  const intro = bookendScene(
    INTRO_SCENE_ID,
    doc.title,
    n.intro,
    doc.bookends?.introAudioAssetId,
    INTRO_MS,
  );
  const outro = bookendScene(
    OUTRO_SCENE_ID,
    'Générique',
    n.outro,
    // la voix enregistrée du générique dit le message de la vidéo : vidéo seulement
    mode === 'video' ? doc.bookends?.outroAudioAssetId : null,
    OUTRO_MS,
  );
  return { ...doc, scenes: [...hook, intro, ...doc.scenes, outro] };
}
